import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { GovWizard } from '@gov-design-system-ce/react';
import { GovFormWeb } from '@gov-design-system-ce/react-form';
import { MarkdownRender } from '@gov-nx/component/web';
import {
	useAuthorizedLabel,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import { useEPeticeEditContext } from '@gov-nx/module/page';
import { Button, Dialog, ErrorStatusBanner } from '@gov-nx/ui/web';
import { pageRouteResolver } from '@gov-nx/web';
import { SouhlasCheckbox } from '../../../Autorizace/SouhlasCheckbox';
import { WizardStepWeb } from '../../../Wizard/WizardStepWeb';
import { EPetitionFormSkeleton } from '../form/EPetitionFormSkeleton';
import { WizardStep1 } from '../form/WizardStep1';
import { WizardStep2 } from '../form/WizardStep2';
import { WizardStep3 } from '../form/WizardStep3';
import { WizardStep4 } from '../form/WizardStep4';
import { ContactAddressForm } from './ContactAddressForm';
import { PetitionMemberForm } from './PetitionMemberForm';

const CREATE_FORM_ID = 'EPETICE_CREATE_FORM';

export const EPetitionEditView = () => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const {
		code,
		addressLookup,
		controls,
		personCommunicationData,
		isPetitionMemberAddressLookupModalOpen,
		isSubmitConfirmModalOpen,
		isDeleteConfirmModalOpen,
		wizard,
		formStep1,
		formStep2,
		petitionId,
		zakladatelTyp,
		isProcessing,
		isEditable,
		applicant,
		onDelete,
		isLowAuthorization,
		isAuthorizationEnforced,
		createFormDefinition,
		handleCreateSubmit,
	} = useEPeticeEditContext();

	const tsn = getLocalizeCurried(code);
	const authLabel = useAuthorizedLabel();
	const navigate = useNavigate();

	if (controls.initialLoading || !formStep2) {
		return <EPetitionFormSkeleton />;
	}

	if (controls.initialError) {
		return <ErrorStatusBanner code={code} />;
	}

	if (!isEditable) {
		return (
			<Navigate to={pageRouteResolver('epetice.detail', { petitionId })} />
		);
	}

	return (
		<div>
			<Dialog
				isOpen={
					zakladatelTyp === 'FO' &&
					personCommunicationData.control.isEditingAddress.value
				}
				onClose={personCommunicationData.control.isEditingAddress.setFalse}
				title={<h3>{tsn('formular.nadpis.editace-adresy')}</h3>}>
				<ContactAddressForm
					code={code}
					addressLookup={addressLookup}
				/>
			</Dialog>

			<Dialog
				isOpen={isPetitionMemberAddressLookupModalOpen.value}
				onClose={isPetitionMemberAddressLookupModalOpen.setFalse}
				title={<h3>{tsn('formular.nadpis.pridat-clena-vyboru')}</h3>}>
				<PetitionMemberForm />
			</Dialog>

			<Dialog
				isOpen={isSubmitConfirmModalOpen.value}
				onClose={() => {
					isSubmitConfirmModalOpen.setFalse();
					createFormDefinition.formMethods.reset();
				}}
				title={<h3>{tsn('formular.nadpis.opravdu-zalozit')}</h3>}
				footer={
					<Button
						form={CREATE_FORM_ID}
						nativeType="submit"
						isDisabled={isProcessing}
						isLoading={controls.createLoading}
						aria-label={authLabel(
							tsn('akce.wcag.zalozit'),
							isAuthorizationEnforced
						)}
						color="primary"
						type="solid"
						size="m">
						{authLabel(
							tsn('formular.tlacitko.zalozit'),
							isAuthorizationEnforced
						)}
					</Button>
				}>
				<GovFormWeb
					id={CREATE_FORM_ID}
					formDefinition={createFormDefinition}
					onSubmit={handleCreateSubmit(() => {
						navigate(pageRouteResolver('epetice.detail', { petitionId }));
					})}>
					<MarkdownRender>
						{tsn('formular.popis.opravdu-zalozit')}
					</MarkdownRender>
					<SouhlasCheckbox
						show={isLowAuthorization}
						disabled={isProcessing}
					/>
				</GovFormWeb>
			</Dialog>

			<Dialog
				isOpen={isDeleteConfirmModalOpen.value}
				onClose={isDeleteConfirmModalOpen.setFalse}
				title={<h3>{tsn('akce.opravdu-chcete-smazat')}</h3>}
				footer={
					<>
						<Button
							onClick={() => {
								onDelete(() => {
									isDeleteConfirmModalOpen.setFalse();
									navigate(pageRouteResolver('epetice'));
								});
							}}
							isLoading={controls.deleteLoading}
							aria-label={tsn('akce.wcag.smazat')}
							isDisabled={isProcessing}
							color="error"
							size="l"
							type="solid">
							{tsn('formular.tlacitko.smazat')}
						</Button>
						<Button
							onClick={isDeleteConfirmModalOpen.setFalse}
							aria-label={tsn('akce.wcag.smazat')}
							isDisabled={isProcessing}
							color="primary"
							size="l"
							type="outlined">
							{tsn('formular.tlacitko.zrusit')}
						</Button>
					</>
				}>
				<p>{tsn('akce.opravdu-chcete-smazat')}</p>
			</Dialog>

			<GovWizard size="l">
				<WizardStepWeb
					index={0}
					label={tsn('formular.zprava.zakladni-informace')}
					wizard={wizard}>
					<WizardStep1
						formDefinition={formStep1}
						isLoading={false}
						isDisabled={true}
						applicant={applicant}
						code={code}
					/>
				</WizardStepWeb>
				<WizardStepWeb
					index={1}
					label={tsn('formular.zprava.informace-o-petici')}
					wizard={wizard}>
					<WizardStep2 />
				</WizardStepWeb>
				<WizardStepWeb
					index={2}
					label={tsn('formular.zprava.peticni-vybor')}
					wizard={wizard}>
					<WizardStep3 />
				</WizardStepWeb>
				<WizardStepWeb
					index={3}
					label={tsn('formular.zprava.rekapitulace')}
					wizard={wizard}>
					<WizardStep4 />
				</WizardStepWeb>
			</GovWizard>
		</div>
	);
};
